import {
  toWSDateString,
  toWSMoneyString,
  useWSSnackbar,
  WSButton,
  WSDrawer,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSMessageBox,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  DeductionType,
  IPayableSchema
} from "@wingspanhq/payments/dist/interfaces";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces/invoice";
import { AxiosError } from "axios";
import React, { useState } from "react";
import { QueryConfig } from "react-query";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { Card } from "../../../ClientPayments/components/Card/Card";
import { Fullscreen } from "../../../components/Fullscreen/Fullscreen";
import { PublicAttachmentDownloadLink } from "../../../components/PublicAttachmentDownloadLink/PublicAttachmentDownloadLink";
import { useWSQuery } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYABLE } from "../../../query/payments/keys";
import { useSendCollaboratorInvite } from "../../../query/payments/mutations";
import {
  useClientDeductions,
  useCollaboratorDeductions,
  useCollaboratorQuery
} from "../../../query/payments/queries";
import {
  getCollaboratorCompany,
  getCollaboratorName
} from "../../../query/payments/selectors";
import { useUserProfile } from "../../../query/users/queries";
import { getUserName } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { getParentPath } from "../../../utils/goToParentRoute";
import {
  InvoiceActivity,
  InvoiceDetailsActivity
} from "../../components/InvoiceDetailsActivity/InvoiceDetailsActivity";
import { PayableDetailsHeader } from "../../components/PayableDetailsHeader/PayableDetailsHeader";
import { Totals, TotalsSection } from "../../components/Totals/Totals";
import { calculateLineItemsTotal } from "../../utils";
import { getLineItemsTotals } from "../../utils/getLineItemsTotals";
import styles from "../InvoicesInvoiceDetails.module.scss";
import { QuickbooksWrapper } from "../../components/QuickbooksWrapper/QuickbooksWrapper";
import { QuickbooksHistory } from "../../components/QuickbooksHistory/QuickbooksHistory";
import {
  PaidDeductionsList,
  UnpaidDeductionsList
} from "../../components/Deductions/DeductionsList";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

export const usePayableQuery = (
  payableId: string,
  config?: QueryConfig<IPayableSchema, AxiosError>
) =>
  useWSQuery<IPayableSchema, AxiosError>(
    [QUERY_PAYABLE, payableId],
    () => paymentsService.payable.get(payableId),
    {
      ...config
    }
  );

type Props = RouteComponentProps<
  { payableId: string },
  {},
  { backPath?: string }
>;

export const PayableDetails: React.FC<Props> = ({
  match,
  history,
  location
}) => {
  const backPath = location.state?.backPath || getParentPath(history);

  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search
      });
    }
  };

  return (
    <>
      <WSScreen.Mobile>
        <Fullscreen>
          <Inner payableId={match.params.payableId} onBack={onBack} />
        </Fullscreen>
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <WSDrawer onClose={onBack} className={styles.drawer} size="S">
          <Inner payableId={match.params.payableId} onBack={onBack} />
        </WSDrawer>
      </WSScreen.TabletAndDesktop>
    </>
  );
};

type InnerProps = {
  payableId: string;
  onBack: () => void;
};

const Inner: React.FC<InnerProps> = ({ payableId, onBack }) => {
  const history = useHistory();
  const [detailsVisible, setDetailsVisible] = useState(true);
  const payableQuery = usePayableQuery(payableId);
  const collaboratorQuery = useCollaboratorQuery(
    payableQuery.data?.collaboratorId as string,
    {
      retry: false,
      enabled: !!payableQuery.data?.collaboratorId
    }
  );
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const [sendCollaboratorInvite] = useSendCollaboratorInvite();
  const { openSnackbar } = useWSSnackbar();

  const qDeductions = useCollaboratorDeductions(
    {
      memberId: payableQuery.data?.memberId as string,
      clientId: payableQuery.data?.clientId as string,
      type: DeductionType.PostPayment
    },
    {
      enabled: !!payableQuery.data
    }
  );

  return (
    <WSElement className={styles.wrapper}>
      <WSQueries
        queries={{ payableQuery, collaboratorQuery, userQuery, qDeductions }}
      >
        {({
          payableQuery: { data: payable },
          collaboratorQuery: { data: collaborator },
          userQuery: { data: user },
          qDeductions: { data: deductions }
        }) => {
          const totalsSections: TotalsSection[] = [];

          const totalsLineItemsSection = getLineItemsTotals(payable.lineItems);

          deductions = deductions.filter(deduction => {
            if (deduction.application?.length > 0) {
              return deduction.application.some(
                application => application.payableId === payable.payableId
              );
            }

            return false;
          });

          if (payable.chargedFees?.lateFee) {
            totalsLineItemsSection.items.push([
              "Late fee",
              toWSMoneyString(payable.chargedFees?.lateFee.amount)
            ]);
          }

          totalsSections.push(totalsLineItemsSection);

          totalsSections.push({
            items: [
              {
                bold: true,
                left: "Total",
                right: toWSMoneyString(
                  calculateLineItemsTotal(payable.lineItems) +
                    (payable.chargedFees?.lateFee?.amount || 0)
                )
              },
              ...deductions.map(d => ({
                left: d.name,
                right: toWSMoneyString(-d.amount)
              }))
            ]
          });

          if (deductions.length) {
            totalsSections.push({
              items: [
                {
                  bold: true,
                  left: "Contractor gross income",
                  right: toWSMoneyString(
                    calculateLineItemsTotal(payable.lineItems) +
                      (payable.chargedFees?.lateFee?.amount || 0) -
                      deductions.reduce(
                        (partialSum, a) => partialSum + a.amount,
                        0
                      )
                  )
                }
              ]
            });
          }

          const activities: InvoiceActivity[] = [];

          activities.push({
            title: "Payable was created",
            description: toWSDateString(payable.createdAt),
            date: payable.createdAt
          });

          if (payable.events.openedAt) {
            activities.push({
              title: `Sent to ${user.email}`,
              description: toWSDateString(payable.events.openedAt),
              date: payable.events.openedAt
            });
          }

          if (
            payable.events.emailReceivedAt &&
            payable.events.emailReceivedAt.length > 0
          ) {
            activities.push({
              title: `Received by ${user.email}`,
              description: toWSDateString(payable.events.emailReceivedAt[0]),
              date: payable.events.emailReceivedAt[0]
            });
          }

          // Work with viewed at only if it is before paidAt and depositedAt
          // Viewed event should never come after paid and deposited
          const allowedViewedAt = (
            payable.events.emailViewedAt || []
          ).filter(date =>
            payable.events.paidAt
              ? payable.events.paidAt > date
              : true && payable.events.depositedAt
              ? payable.events.depositedAt > date
              : true
          );

          if (allowedViewedAt.length > 0) {
            allowedViewedAt.sort((a, b) => b.getTime() - a.getTime());

            const firstViewedAt = allowedViewedAt[allowedViewedAt.length - 1];

            if (firstViewedAt) {
              activities.push({
                title: `Viewed by ${user.email}`,
                description: toWSDateString(firstViewedAt),
                date: firstViewedAt
              });
            }

            const secondViewedAt = firstViewedAt
              ? allowedViewedAt.find(
                  date =>
                    date.getTime() - firstViewedAt.getTime() > 60 * 60 * 1000
                )
              : undefined;

            if (secondViewedAt) {
              activities.push({
                title: `Viewed by ${user.email}`,
                description: toWSDateString(secondViewedAt),
                date: secondViewedAt
              });
            }
          }

          if (payable.events.approvedAt) {
            activities.push({
              title: `Approved by ${user.email}`,
              description: toWSDateString(payable.events.approvedAt),
              date: payable.events.approvedAt
            });
          }

          if (payable.events.sentDueIn3DaysAt) {
            activities.push({
              title: `Reminder sent to ${user.email}`,
              description: toWSDateString(payable.events.sentDueIn3DaysAt),
              date: payable.events.sentDueIn3DaysAt
            });
          }

          if (payable.events.sentDueTodayAt) {
            activities.push({
              title: `Reminder sent to ${user.email}`,
              description: toWSDateString(payable.events.sentDueTodayAt),
              date: payable.events.sentDueTodayAt
            });
          }

          if (payable.events.sentDue3DaysAgoAt) {
            activities.push({
              title: `Reminder sent to ${user.email}`,
              description: toWSDateString(payable.events.sentDue3DaysAgoAt),
              date: payable.events.sentDue3DaysAgoAt
            });
          }

          if (payable.events.sentDue7DaysAgoAt) {
            activities.push({
              title: `Reminder sent to ${user.email}`,
              description: toWSDateString(payable.events.sentDue7DaysAgoAt),
              date: payable.events.sentDue7DaysAgoAt
            });
          }

          if (payable.events.paymentInTransitAt) {
            activities.push({
              title: `Payment in transit on ${toWSDateString(
                payable.events.paymentInTransitAt
              )}`,
              description: "Waiting on wire transfer to arrive",
              date: payable.events.paymentInTransitAt
            });
          }

          if (payable.events.paidAt) {
            activities.push({
              title: `Paid on ${toWSDateString(payable.events.paidAt)}`,
              description: payable.events.estimatedDepositAt
                ? `Expected deposit on ${toWSDateString(
                    payable.events.estimatedDepositAt,
                    "monthDayYear"
                  )}`
                : "Payable was paid outside of Wingspan",
              date: payable.events.paidAt
            });
          }

          if (payable.events.memberDisputedAt) {
            activities.push({
              title: `Disputed by ${collaborator.member.user.email}`,
              description: `Dispute reason: ${payable.member.comment}`,
              date: payable.events.memberDisputedAt
            });
          }

          if (payable.events.clientResolvedDisputeAt) {
            activities.push({
              title: "Dispute resolved",
              description: `Invoice dispute was resolved`,
              date: payable.events.clientResolvedDisputeAt
            });
          }

          if (payable.events.clientDeclinedAt) {
            activities.push({
              title: `Rejected by ${user.email}`,
              description: `Reject reason: ${payable.client.comment}`,
              date: payable.events.clientDeclinedAt
            });
          }

          if (payable.events.memberResubmittedAt) {
            activities.push({
              title: `Resubmitted by ${collaborator.member.user.email}`,
              description: `Member resubmitted invoice`,
              date: payable.events.memberResubmittedAt
            });
          }

          activities.sort((a, b) => {
            if (a.date > b.date) return -1;
            if (a.date < b.date) return 1;
            return 0;
          });

          return (
            <>
              <WSElement className={styles.header}>
                <WSIcon
                  name="back"
                  className={styles.backIcon}
                  onClick={onBack}
                />
                <WSText.Heading4 data-testid="payableDetailsHeader">
                  Invoice #{payable.invoiceNumber || " Pending"}
                </WSText.Heading4>
              </WSElement>
              <WSElement className={styles.main}>
                <Card mb="M">
                  <WSText.Heading2 mb="S">
                    {getCollaboratorCompany(collaborator)}
                  </WSText.Heading2>
                  <PayableDetailsHeader payableId={payableId} />

                  {payable.status === InvoiceStatus.Pending ? (
                    <WSMessageBox.Warning noBorder mt="M">
                      <WSFlexBox.Center>
                        <WSFlexBox.CenterY mt="S">
                          <WSIcon
                            name="warning"
                            size="S"
                            color="yellow500"
                            mr="S"
                          />
                          <WSText.Heading4 inline>
                            Not yet eligible for payments
                          </WSText.Heading4>
                        </WSFlexBox.CenterY>
                      </WSFlexBox.Center>
                      <WSText.Label mt="M">
                        Your invite was sent. We're still waiting for this
                        person to complete their onboarding requirements before
                        they can receive payments.
                      </WSText.Label>
                      {/*<WSButton.Warning
                        mt="M"
                        fullWidth
                        onClick={() => {
                          sendCollaboratorInvite(collaborator.collaboratorId, {
                            onSuccess: () => {
                              openSnackbar({
                                message: `Invite re-sent to ${collaborator
                                  .member.user.profile?.firstName ||
                                  collaborator.member.user.email}`
                              });
                            }
                          });
                        }}
                      >
                        Resend invite
                      </WSButton.Warning>
                      */}
                    </WSMessageBox.Warning>
                  ) : null}
                </Card>

                <Card mb="M">
                  <WSFlexBox wrap="nowrap" justify="space-between">
                    <WSButton.Link
                      onClick={() => {
                        setDetailsVisible(!detailsVisible);
                      }}
                      rightIcon={detailsVisible ? "dropUp" : "dropDown"}
                    >
                      {detailsVisible ? "Hide" : "Show"} invoice details
                    </WSButton.Link>
                    <WSText.Heading3>
                      {toWSMoneyString(
                        calculateLineItemsTotal(payable.lineItems)
                      )}
                    </WSText.Heading3>
                  </WSFlexBox>
                  {detailsVisible && (
                    <WSElement mt="M">
                      <WSElement mb="M">
                        <WSText.Label mb="XS" color="grey600">
                          Bill to
                        </WSText.Label>
                        <WSText mb="M">{getUserName(user)}</WSText>
                        <WSText.Label mb="XS" color="grey600">
                          From
                        </WSText.Label>
                        <WSText mb="M">
                          {getCollaboratorCompany(collaborator) ||
                            getCollaboratorName(collaborator)}
                        </WSText>
                      </WSElement>

                      <WSElement mb="M">
                        <WSText.Label mb="XS" color="grey600">
                          Pay date
                        </WSText.Label>
                        <WSText>
                          {toWSDateString(
                            payable.client?.payDate || payable.dueDate,
                            "monthDayYear"
                          )}
                        </WSText>
                      </WSElement>
                      {payable.metadata?.purchaseOrderNumber ? (
                        <WSElement mb="M">
                          <WSText.Label mb="XS" color="grey600">
                            PO Number
                          </WSText.Label>
                          <WSText>
                            {payable.metadata?.purchaseOrderNumber}
                          </WSText>
                        </WSElement>
                      ) : null}
                      <WSElement mb="M">
                        <WSText.Label mb="XS" color="grey600">
                          Due date
                        </WSText.Label>
                        <WSText>
                          {toWSDateString(payable.dueDate, "monthDayYear")}
                        </WSText>
                      </WSElement>
                      {payable.labels?.projectName ? (
                        <WSElement mb="M">
                          <WSText.Label mb="XS" color="grey600">
                            Project Name
                          </WSText.Label>
                          <WSText>{payable.labels?.projectName}</WSText>
                        </WSElement>
                      ) : null}
                      {payable.invoiceNotes ? (
                        <WSElement mb="M">
                          <WSText.Label mb="XS" color="grey600">
                            Notes
                          </WSText.Label>
                          <WSText>{payable.invoiceNotes}</WSText>
                        </WSElement>
                      ) : null}

                      <WSElement>
                        <Totals sections={totalsSections} />
                      </WSElement>

                      {payable.lateFeeHandling &&
                        (!!payable.lateFeeHandling.lateFeePercentage ||
                          !!payable.lateFeeHandling.lateFeeAmount) && (
                          <WSElement mt="M">
                            <WSText.Label mb="XS" color="grey600">
                              Late fee
                            </WSText.Label>
                            <WSText>
                              {payable.lateFeeHandling.lateFeePercentage
                                ? `${payable.lateFeeHandling.lateFeePercentage}%`
                                : toWSMoneyString(
                                    payable.lateFeeHandling.lateFeeAmount
                                  )}{" "}
                              every {payable.lateFeeHandling.frequency.every}{" "}
                              {payable.lateFeeHandling.frequency.interval}{" "}
                              overdue
                            </WSText>
                          </WSElement>
                        )}

                      {payable.attachments?.customAttachmentIds && (
                        <WSElement mt="M">
                          {payable.attachments?.customAttachmentIds.map(id => (
                            <PublicAttachmentDownloadLink id={id} mb="S" />
                          ))}
                        </WSElement>
                      )}
                    </WSElement>
                  )}
                </Card>

                {payable.status === InvoiceStatus.Paid ? (
                  <PaidDeductionsList
                    payableId={payable.payableId}
                    collaboratorId={payable.collaboratorId}
                    clientId={payable.clientId}
                    memberId={payable.memberId}
                    onRowClick={d => {
                      history.push(
                        `${history.location.pathname}/deduction/scheduled/${d.deductionId}?${history.location.search}`
                      );
                    }}
                  />
                ) : (
                  <UnpaidDeductionsList
                    collaboratorId={payable.collaboratorId}
                    clientId={payable.clientId}
                    memberId={payable.memberId}
                    onRowClick={d => {
                      history.push(
                        `${history.location.pathname}/deduction/scheduled/${d.deductionId}?${history.location.search}`
                      );
                    }}
                  />
                )}

                <Card>
                  <WSText.Heading3 mb="M">All activity</WSText.Heading3>
                  <InvoiceDetailsActivity activities={activities} />
                </Card>
                <QuickbooksWrapper>
                  <QuickbooksHistory
                    Wrapper={Card}
                    mt="M"
                    entityId={payable.payableId}
                  />
                </QuickbooksWrapper>
              </WSElement>
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
